import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PartnerContextHeader from '../../components/ui/PartnerContextHeader';
import StrategicAlignmentPanel from './components/StrategicAlignmentPanel';
import JointMarketingSection from './components/JointMarketingSection';
import RevenueTargetsTracker from './components/RevenueTargetsTracker';
import StrategicFitRadar from './components/StrategicFitRadar';
import GrowthOpportunityPanel from './components/GrowthOpportunityPanel';
import Icon from '../../components/AppIcon';
import { growthOpportunitiesService, revenueTargetsService } from '../../services/growthService';
import { marketingCampaignsService } from '../../services/marketingCampaignsService';
import { partnerService } from '../../services/partnerService';
import { usePartner } from '../../contexts/PartnerContext';
import { exportToPDF } from '../../utils/exportUtils';
import GrowthPartnerSwitcher from './components/GrowthPartnerSwitcher';

// ─── Add Growth Opportunity Modal ─────────────────────────────────────────────
const AddGrowthOpportunityModal = ({ onClose, onSuccess, partners }) => {
  const [form, setForm] = useState({
    title: '',
    partner_id: '',
    opp_type: 'Upsell',
    potential_revenue: '',
    target_quarter: '',
    owner: '',
    priority: 'medium',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await growthOpportunitiesService?.create({
        title: form?.title,
        partner_id: form?.partner_id || null,
        opp_type: form?.opp_type,
        potential_revenue: parseFloat(form?.potential_revenue) || 0,
        target_quarter: form?.target_quarter,
        owner: form?.owner,
        priority: form?.priority,
        description: form?.description,
        category: 'market-expansion',
        status: 'open',
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create opportunity.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-warning/5 to-destructive/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-destructive flex items-center justify-center">
              <Icon name="Lightbulb" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Add Growth Opportunity</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-28 space-y-16 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Title *</label>
            <input name="title" value={form?.title} onChange={handleChange} placeholder="e.g. Healthcare IoT Expansion" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" required />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Partner</label>
              <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                <option value="">— Select —</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Type</label>
              <select name="opp_type" value={form?.opp_type} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['Upsell', 'Cross-sell', 'New Logo', 'Expansion', 'Renewal']?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Target Revenue ($)</label>
              <input name="potential_revenue" type="number" min="0" value={form?.potential_revenue} onChange={handleChange} placeholder="e.g. 500000" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Target Quarter</label>
              <input name="target_quarter" value={form?.target_quarter} onChange={handleChange} placeholder="e.g. Q3 2026" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Priority</label>
              <select name="priority" value={form?.priority} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['low', 'medium', 'high']?.map(p => <option key={p} value={p}>{p?.charAt(0)?.toUpperCase() + p?.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Owner</label>
              <input name="owner" value={form?.owner} onChange={handleChange} placeholder="e.g. John Smith" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Description</label>
            <textarea name="description" value={form?.description} onChange={handleChange} rows={3} placeholder="Describe this growth opportunity..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" />
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-warning to-destructive text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Plus" size={16} /> Add Opportunity</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Add Revenue Target Modal ─────────────────────────────────────────────────
const AddRevenueTargetModal = ({ onClose, onSuccess, partners }) => {
  const [form, setForm] = useState({
    partner_id: '',
    year: new Date()?.getFullYear()?.toString(),
    quarter: 'Q1',
    target_amount: '',
    stretch_goal: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.target_amount) { setError('Target amount is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await revenueTargetsService?.create({
        partner_id: form?.partner_id || null,
        year: parseInt(form?.year),
        quarter: form?.quarter,
        target_amount: parseFloat(form?.target_amount),
        stretch_goal: parseFloat(form?.stretch_goal) || 0,
        achieved_amount: 0,
        notes: form?.notes,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create revenue target.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="TrendingUp" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Add Revenue Target</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-28 space-y-16">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Partner</label>
            <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
              <option value="">— All Partners —</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Year</label>
              <input name="year" type="number" min="2020" max="2030" value={form?.year} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Quarter</label>
              <select name="quarter" value={form?.quarter} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['Q1', 'Q2', 'Q3', 'Q4', 'Annual']?.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Target Amount ($) *</label>
              <input name="target_amount" type="number" min="0" value={form?.target_amount} onChange={handleChange} placeholder="e.g. 1250000" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Stretch Goal ($)</label>
              <input name="stretch_goal" type="number" min="0" value={form?.stretch_goal} onChange={handleChange} placeholder="e.g. 1500000" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Notes</label>
            <textarea name="notes" value={form?.notes} onChange={handleChange} rows={2} placeholder="Any additional notes..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" />
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Plus" size={16} /> Add Target</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Collapsible Section Wrapper ──────────────────────────────────────────────
const CollapsibleSection = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-24 py-16 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-12">
          <Icon name={icon} size={20} className="text-primary" />
          <span className="text-base font-semibold text-slate-800">{title}</span>
        </div>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-slate-400" />
      </button>
      {open && <div className="p-24 pt-0">{children}</div>}
    </div>
  );
};

// ─── Page-level KPI Bar ───────────────────────────────────────────────────────
const KPIBar = ({ opportunities, targets, campaigns }) => {
  const totalPipeline = opportunities?.reduce((s, o) => s + (o?.potential_revenue || o?.potentialRevenue || 0), 0);
  const activeCampaigns = campaigns?.filter(c => c?.status === 'active')?.length;
  const openOpps = opportunities?.filter(o => o?.status === 'open' || !o?.status)?.length;

  const annualTarget = targets?.find(t => t?.quarter === 'Annual') || targets?.[0];
  const targetPct = annualTarget
    ? Math.round(((annualTarget?.achieved_amount || 0) / (annualTarget?.target_amount || 1)) * 100)
    : 0;

  const kpis = [
    { label: 'Total Pipeline', value: `OMR ${(totalPipeline / 1000000)?.toFixed(1)}M`, icon: 'DollarSign', color: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    { label: 'Active Campaigns', value: activeCampaigns, icon: 'Megaphone', color: 'from-blue-500 to-indigo-500', bg: 'bg-blue-50', border: 'border-blue-200' },
    { label: 'Open Opportunities', value: openOpps, icon: 'Lightbulb', color: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200' },
    { label: 'Annual Target %', value: `${targetPct}%`, icon: 'Target', color: 'from-violet-500 to-purple-500', bg: 'bg-violet-50', border: 'border-violet-200' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-16 mb-32">
      {kpis?.map((kpi, i) => (
        <div key={i} className={`${kpi?.bg} ${kpi?.border} border rounded-2xl p-20 flex items-center gap-16`}>
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${kpi?.color} flex items-center justify-center flex-shrink-0`}>
            <Icon name={kpi?.icon} size={22} className="text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{kpi?.value}</div>
            <div className="text-xs text-slate-500 font-medium mt-1">{kpi?.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const StrategicGrowthPlanning = () => {
  const { selectedPartnerId, selectedPartner, switchPartner } = usePartner();
  const navigate = useNavigate();

  // Local active partner — defaults to context, but can be switched independently on this page
  const [activePartnerId, setActivePartnerId] = useState(selectedPartnerId);
  const [activePartner, setActivePartner] = useState(selectedPartner);

  const [partnerData, setPartnerData] = useState({
    strategicTier: 'gold',
    industryFocus: ['telecom', 'finance', 'healthcare'],
    roadmapFit: { technology: 75, market: 80, innovation: 65, sustainability: 70 }
  });
  const [campaigns, setCampaigns] = useState([]);
  const [targets, setTargets] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddOppModal, setShowAddOppModal] = useState(false);
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);

  // Sync activePartner object whenever activePartnerId or partnersList changes
  useEffect(() => {
    if (activePartnerId && partnersList?.length > 0) {
      const found = partnersList?.find(p => p?.id === activePartnerId);
      if (found) setActivePartner(found);
    } else if (!activePartnerId) {
      setActivePartner(null);
    }
  }, [activePartnerId, partnersList]);

  // When context partner changes externally, sync local state
  useEffect(() => {
    if (selectedPartnerId && selectedPartnerId !== activePartnerId) {
      setActivePartnerId(selectedPartnerId);
    }
  }, [selectedPartnerId]);

  const handleSwitchPartner = (partnerId) => {
    setActivePartnerId(partnerId);
    // Also update global context so other pages stay in sync
    if (switchPartner) switchPartner(partnerId);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [opps, tgts, cmpns, partners] = await Promise.all([
        growthOpportunitiesService?.getAll(activePartnerId),
        revenueTargetsService?.getAll(activePartnerId),
        marketingCampaignsService?.getAll(activePartnerId),
        partnerService?.getAll(),
      ]);
      setOpportunities(opps || []);
      setTargets(tgts || []);
      setCampaigns(cmpns || []);
      setPartnersList(partners || []);
    } catch (err) {
      console.error('Error loading growth data:', err);
    } finally {
      setLoading(false);
    }
  }, [activePartnerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateAlignment = (updates) => setPartnerData(prev => ({ ...prev, ...updates }));

  const handleCreateCampaign = async (campaign) => {
    try {
      const saved = await marketingCampaignsService?.create({
        ...campaign,
        partner_id: activePartnerId || null,
        budget: parseFloat(campaign?.budget) || 0,
        progress: 0,
        leads: 0,
        assets: 0,
      });
      setCampaigns(prev => [saved, ...prev]);
    } catch (err) {
      console.error('Failed to save campaign:', err);
      setCampaigns(prev => [{ ...campaign, id: Date.now() }, ...prev]);
    }
  };

  const handleUpdateCampaign = async (campaignId, updates) => {
    try {
      const saved = await marketingCampaignsService?.update(campaignId, updates);
      setCampaigns(prev => prev?.map(c => c?.id === campaignId ? saved : c));
    } catch (err) {
      setCampaigns(prev => prev?.map(c => c?.id === campaignId ? { ...c, ...updates } : c));
    }
  };

  const handleUpdateTargets = (newTargets) => setTargets(newTargets);

  const handleExport = () => {
    const rows = opportunities?.map(o => ({
      title: o?.title || '',
      status: o?.status || 'open',
      priority: o?.priority || '',
      potential_revenue: o?.potential_revenue || o?.potentialRevenue || 0,
      target_quarter: o?.target_quarter || '',
      owner: o?.owner || '',
    }));
    exportToPDF({
      title: `Strategic Growth Plan — ${activePartner?.brand_name || activePartner?.legal_name || 'All Partners'}`,
      subtitle: `Generated ${new Date()?.toLocaleDateString()}`,
      rows,
      columns: ['title', 'status', 'priority', 'potential_revenue', 'target_quarter', 'owner'],
      headers: {
        title: 'Opportunity',
        status: 'Status',
        priority: 'Priority',
        potential_revenue: 'Revenue ($)',
        target_quarter: 'Quarter',
        owner: 'Owner',
      },
      summaryCards: [
        { label: 'Total Opportunities', value: opportunities?.length },
        { label: 'Pipeline Value', value: `OMR ${(opportunities?.reduce((s, o) => s + (o?.potential_revenue || 0), 0) / 1000000)?.toFixed(1)}M` },
        { label: 'Active Campaigns', value: campaigns?.filter(c => c?.status === 'active')?.length },
      ],
    });
  };

  return (
    <>
      <Helmet>
        <title>Strategic Growth Planning - PartnerHub</title>
        <meta name="description" content="Align partnership strategies and track revenue targets through comprehensive strategic assessment and joint marketing coordination" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/20">
        <Header />
        <PartnerContextHeader />

        {/* Partner Switcher Bar */}
        <GrowthPartnerSwitcher
          partners={partnersList}
          selectedPartnerId={activePartnerId}
          selectedPartner={activePartner}
          onSelectPartner={handleSwitchPartner}
          targets={targets}
          loading={loading && partnersList?.length === 0}
        />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">
            {/* Page Header */}
            <div className="mb-24">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-16">
                <div>
                  <div className="flex items-center gap-12 mb-12">
                    <div className="p-12 bg-gradient-to-br from-warning to-destructive rounded-xl shadow-lg">
                      <Icon name="TrendingUp" size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                        Strategic Growth Planning
                      </h1>
                      <p className="text-slate-600 mt-4">
                        Partnership alignment and revenue optimization
                        {activePartner && <span className="ml-8 text-primary font-medium">— {activePartner?.brand_name || activePartner?.legal_name}</span>}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-12 flex-wrap">
                  <button
                    onClick={() => setShowAddOppModal(true)}
                    className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-warning to-destructive text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    <Icon name="Lightbulb" size={16} />
                    Add Opportunity
                  </button>
                  <button
                    onClick={() => setShowAddTargetModal(true)}
                    className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    <Icon name="TrendingUp" size={16} />
                    Add Revenue Target
                  </button>
                  <button
                    onClick={handleExport}
                    className="flex items-center gap-8 px-20 py-11 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    <Icon name="Download" size={16} />
                    Export Report
                  </button>
                </div>
              </div>
            </div>

            {/* KPI Summary Bar */}
            <KPIBar opportunities={opportunities} targets={targets} campaigns={campaigns} />

            {loading ? (
              <div className="flex items-center justify-center py-64">
                <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                <span className="ml-16 text-slate-500 text-sm">Loading growth data...</span>
              </div>
            ) : (
              <div className="space-y-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                  <CollapsibleSection title="Strategic Alignment" icon="Target">
                    <StrategicAlignmentPanel
                      partnerData={partnerData}
                      onUpdate={handleUpdateAlignment}
                      enablementScore={activePartner?.enablement_score || null}
                    />
                  </CollapsibleSection>
                  <CollapsibleSection title="Strategic Fit Analysis" icon="Radar">
                    <StrategicFitRadar
                      partnerData={partnerData}
                      enablementScores={activePartner?.capability_scores || null}
                    />
                  </CollapsibleSection>
                </div>

                <CollapsibleSection title="Revenue Targets" icon="TrendingUp">
                  <RevenueTargetsTracker
                    targets={targets}
                    onUpdateTargets={handleUpdateTargets}
                    onRefresh={loadData}
                  />
                </CollapsibleSection>

                <CollapsibleSection title="Joint Marketing" icon="Megaphone">
                  <JointMarketingSection
                    campaigns={campaigns}
                    onCreateCampaign={handleCreateCampaign}
                    onUpdateCampaign={handleUpdateCampaign}
                    partnerId={activePartnerId}
                  />
                </CollapsibleSection>

                <CollapsibleSection title="Growth Opportunities" icon="Lightbulb">
                  <GrowthOpportunityPanel
                    opportunities={opportunities}
                    onCreateOpportunity={() => setShowAddOppModal(true)}
                    onUpdateOpportunity={async (id, updates) => {
                      try {
                        await growthOpportunitiesService?.update(id, updates);
                        setOpportunities(prev => prev?.map(o => o?.id === id ? { ...o, ...updates } : o));
                      } catch (err) {
                        console.error('Failed to update opportunity:', err);
                      }
                    }}
                    onNavigateToEngagement={() => navigate('/engagement-management-hub')}
                  />
                </CollapsibleSection>
              </div>
            )}
          </div>
        </main>
      </div>

      {showAddOppModal && (
        <AddGrowthOpportunityModal
          onClose={() => setShowAddOppModal(false)}
          onSuccess={() => { setShowAddOppModal(false); loadData(); }}
          partners={partnersList}
        />
      )}
      {showAddTargetModal && (
        <AddRevenueTargetModal
          onClose={() => setShowAddTargetModal(false)}
          onSuccess={() => { setShowAddTargetModal(false); loadData(); }}
          partners={partnersList}
        />
      )}
    </>
  );
};

export default StrategicGrowthPlanning;